/**
 * Form Schema Service
 * Handles all form schema-related API calls
 */

import axiosInstance, { apiRequest } from '../axiosInstance';
import { FORM_SCHEMA_ENDPOINTS, buildQueryString } from '../endpoints';

/**
 * @typedef {import('../../types/formSchema').FormSchemaDefinition} FormSchemaDefinition
 * @typedef {import('../../types/formSchema').CreateFormSchemaDto} CreateFormSchemaDto
 * @typedef {import('../../types/formSchema').UpdateFormSchemaDto} UpdateFormSchemaDto
 */

/**
 * @typedef {Object} FormSchemaListParams
 * @property {boolean} [includeInactive] - Include inactive forms (default: false)
 * @property {string} [category] - Filter by category
 * @property {number} [page] - Page number (default: 1)
 * @property {number} [pageSize] - Page size (default: 20)
 */

const formSchemaService = {
  /**
   * Get list of form schemas
   * @param {FormSchemaListParams} [params] - Query parameters
   * @returns {Promise<Object>} Paginated list of form schemas
   */
  getFormSchemas: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${FORM_SCHEMA_ENDPOINTS.LIST}${queryString}`)
    );
  },

  /**
   * Get list of repeated form schemas (templates)
   * @param {Object} [params] - Query parameters
   * @param {boolean} [params.includeInactive] - Include inactive schemas
   * @returns {Promise<Array>} List of repeated form schemas
   */
  getRepeatedFormSchemas: async (params = {}) => {
    const queryString = buildQueryString(params);
    return apiRequest(() =>
      axiosInstance.get(`${FORM_SCHEMA_ENDPOINTS.REPEATED}${queryString}`)
    );
  },

  /**
   * Get form schema by ID
   * @param {number} id - Form schema ID
   * @returns {Promise<Object>} Form schema data
   */
  getFormSchemaById: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(FORM_SCHEMA_ENDPOINTS.GET_BY_ID(id))
    );
  },

  /**
   * Create new form schema
   * @param {CreateFormSchemaDto} formData - Form schema data
   * @returns {Promise<Object>} Created form schema
   */
  createFormSchema: async (formData) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SCHEMA_ENDPOINTS.CREATE, formData)
    );
  },

  /**
   * Update existing form schema
   * @param {number} id - Form schema ID
   * @param {UpdateFormSchemaDto} formData - Updated form data
   * @returns {Promise<Object>} Updated form schema
   */
  updateFormSchema: async (id, formData) => {
    return apiRequest(() =>
      axiosInstance.put(FORM_SCHEMA_ENDPOINTS.UPDATE(id), formData)
    );
  },

  /**
   * Delete form schema
   * @param {number} id - Form schema ID
   * @returns {Promise<void>}
   */
  deleteFormSchema: async (id) => {
    return apiRequest(() =>
      axiosInstance.delete(FORM_SCHEMA_ENDPOINTS.DELETE(id))
    );
  },

  /**
   * Duplicate form schema
   * @param {number} id - Form schema ID to duplicate
   * @param {string} newName - Name for the duplicated form
   * @returns {Promise<Object>} Duplicated form schema
   */
  duplicateFormSchema: async (id, newName) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SCHEMA_ENDPOINTS.DUPLICATE(id), JSON.stringify(newName), {
        headers: {
          'Content-Type': 'application/json',
        },
      })
    );
  },

  /**
   * Preview form schema with test data
   * @param {number} id - Form schema ID
   * @param {Object} [testData] - Test data for preview
   * @returns {Promise<Object>} Preview data
   */
  previewFormSchema: async (id, testData = {}) => {
    const queryString = buildQueryString({ testData: JSON.stringify(testData) });
    return apiRequest(() =>
      axiosInstance.get(`${FORM_SCHEMA_ENDPOINTS.PREVIEW(id)}${queryString}`)
    );
  },

  /**
   * Helper: Validate form schema data before submission
   * @param {CreateFormSchemaDto|UpdateFormSchemaDto} formData - Form data to validate
   * @returns {Object} Validation result { isValid: boolean, errors: string[] }
   */
  validateFormData: (formData) => {
    const errors = [];

    if (formData.name && formData.name.length > 200) {
      errors.push('Form name must not exceed 200 characters');
    }

    if (formData.description && formData.description.length > 1000) {
      errors.push('Description must not exceed 1000 characters');
    }

    if (formData.category && formData.category.length > 100) {
      errors.push('Category must not exceed 100 characters');
    }

    if (formData.tags && formData.tags.length > 500) {
      errors.push('Tags must not exceed 500 characters');
    }

    if (formData.icon && formData.icon.length > 10) {
      errors.push('Icon must not exceed 10 characters');
    }

    // Validate schemaDefinition if present
    if (formData.schemaDefinition) {
      if (!formData.schemaDefinition.title || formData.schemaDefinition.title.length === 0) {
        errors.push('Schema definition must have a title');
      }

      if (!formData.schemaDefinition.fields || !Array.isArray(formData.schemaDefinition.fields)) {
        errors.push('Schema definition must have a fields array');
      } else if (formData.schemaDefinition.fields.length === 0) {
        errors.push('Schema definition must have at least one field');
      } else {
        // Validate each field
        formData.schemaDefinition.fields.forEach((field, index) => {
          if (!field.key || !/^[a-z][a-z0-9_]*$/.test(field.key)) {
            errors.push(`Field ${index + 1}: key must match pattern ^[a-z][a-z0-9_]*$`);
          }
          if (!field.title || field.title.length === 0) {
            errors.push(`Field ${index + 1}: title is required`);
          }
          if (!field.type) {
            errors.push(`Field ${index + 1}: type is required`);
          }
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  },
  /**
   * Get schema-level suggestions (e.g. should suggest Sample ID field).
   * @param {number} id - Form schema ID
   * @returns {Promise<{shouldSuggestSampleId: boolean, reason: string}>}
   */
  getFormSchemaSuggestions: async (id) => {
    return apiRequest(() =>
      axiosInstance.get(FORM_SCHEMA_ENDPOINTS.SUGGESTIONS(id))
    );
  },

  /**
   * Suppresses the "add Sample ID field" suggestion for this form permanently.
   * @param {number} id - Form schema ID
   */
  suppressSampleIdSuggestion: async (id) => {
    return apiRequest(() =>
      axiosInstance.post(FORM_SCHEMA_ENDPOINTS.SUPPRESS_SAMPLE_ID(id))
    );
  },
};

export default formSchemaService;
